import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  Icon,
  Flex,
  useDisclosure,
  Tooltip,
  Link,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ChevronDownIcon } from '@heroicons/react/solid';
import {
  stagesList,
  userUpdatableStage,
  stageGroup as stageGroupList,
  userUpdatableFlowStage,
  flowStagesList,
} from '@/constants/contact-stages';
import {
  ConfrimIntrested,
  ConfrimNotIntrested,
  StageConfirmation,
} from './StageConfirmation';
import { selectOutreach } from '@/store/outreach/selectors';
import { usePermission } from '@/ui/permission';
import { RestartOutreach } from './Restart';
import { StageList } from '@/components/shared/StageFilter';
import { groupBy } from 'lodash-es';
import { Like } from '@/assets/icons/line';
import { Like as LikeSolid } from '@/assets/icons/bold';
import { HiArrowTopRightOnSquare } from 'react-icons/hi2';
import { FlowRestart } from './flow-restart';
import { StopConfirmation } from './flow-restart/components/StopConfirmation';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';
import { isCandidateAnonymized } from '@/utils/candidateConsent';

export const Stage = ({ currStage, contact }) => {
  const outreach = useSelector(selectOutreach);
  const [stage, setStage] = useState(currStage);
  const [targetStage, setTargetStage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const canUpdateCandidateStage = usePermission({
    permission: 'contact.stage?',
  });

  const stageList = groupBy(userUpdatableStage, 'group');
  const list = Object.entries(stageList);

  const onChange = (stage) => {
    if (stage) {
      setTargetStage(stage);
      onOpen();
    }
  };

  const onSaveSuccess = () => {
    setStage(() => targetStage?.id);
  };

  if (!currStage) {
    return (
      <Flex alignItems="center">
        <KText mr="3px"> Flow Inactive</KText>
      </Flex>
    );
  }

  if (
    outreach?.status === 'completed' ||
    !canUpdateCandidateStage ||
    isCandidateAnonymized(contact)
  ) {
    return (
      <Flex alignItems="center">
        <Box
          width="6px"
          height="6px"
          bg={stagesList[stage]?.color}
          borderRadius="full"
          marginRight="8px"
        />
        <KText mr="3px"> {stagesList[stage]?.label}</KText>
        <Icon
          as={ChevronDownIcon}
          display="none"
          _groupHover={{ display: 'block' }}
        />
      </Flex>
    );
  }

  return (
    <>
      <Flex>
        <Menu isLazy>
          <MenuButton textAlign="left">
            <Flex alignItems="center">
              <Box
                width="6px"
                height="6px"
                bg={stagesList[stage]?.color}
                borderRadius="full"
                marginRight="8px"
              />
              <KText mr="3px"> {stagesList[stage]?.label}</KText>
              <Icon as={ChevronDownIcon} />
            </Flex>
          </MenuButton>
          <MenuList height="300px" width="275px" overflow="auto">
            <StageList
              list={list}
              selected={new Set([stage])}
              stageGroupList={stageGroupList}
              type="updatable"
              onSelect={onChange}
            />
          </MenuList>
        </Menu>
        {contact?.stage === 'outreach_failed' && (
          <RestartOutreach contact={contact} />
        )}
      </Flex>
      {isOpen && (
        <StageConfirmation
          isOpen={isOpen}
          onClose={onClose}
          onSaveSuccess={onSaveSuccess}
          fromStage={stage}
          targetStage={targetStage}
          candidate={contact}
        />
      )}
    </>
  );
};

export const AtsStages = ({
  contact,
  outreach,
  onStageUpdate = () => {},
  isFromAts = false,
}) => {
  const [stage, setStage] = useState(contact?.candidate_stage);
  const [targetStage, setTargetStage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { ats_enabled } = useKFlags();

  const canUpdateCandidateStage = usePermission({
    permission: 'contact.stage?',
  });

  const stageList = groupBy(userUpdatableStage, 'group');
  const list = Object.entries(stageList)?.filter(
    (li) => li?.[0] !== 'outreach'
  );

  const onChange = (stage) => {
    if (stage?.id === contact?.candidate_stage) return;
    if (stage) {
      setTargetStage(stage);
      onOpen();
    }
  };

  const onSaveSuccess = () => {
    setStage(() => targetStage?.id);
    onStageUpdate();
  };

  if (contact?.ats_url) {
    return (
      <Flex alignItems="center">
        {contact?.ats_stage ? (
          <Link
            fontWeight="semiBold"
            href={`${contact?.ats_url}`}
            textDecor="underline"
            isExternal
          >
            <KText
              display="flex"
              alignItems="center"
              gap="6px"
              fontSize={isFromAts ? '14px' : ''}
              p={isFromAts && '2px 8px'}
              border={isFromAts && '1px solid'}
              borderColor={isFromAts && 'border-color'}
              borderRadius={isFromAts && '6px'}
            >
              {contact?.ats_stage}
              <Icon as={HiArrowTopRightOnSquare} />
            </KText>
          </Link>
        ) : (
          <KText
            w="100%"
            alignItems="center"
            gap="6px"
            fontSize={isFromAts ? '14px' : ''}
            p={isFromAts && '2px 8px'}
            border={isFromAts && '1px solid'}
            borderColor={isFromAts && 'border-color'}
            borderRadius={isFromAts && '6px'}
          >
            -
          </KText>
        )}
      </Flex>
    );
  }

  if (ats_enabled) {
    return (
      <KText
        mr="3px"
        size="sm-regular"
        p={isFromAts && '2px 8px'}
        border={isFromAts && '1px solid'}
        borderColor={isFromAts && 'border-color'}
        borderRadius={isFromAts && '6px'}
      >
        {contact?.ats_application?.status === 'rejected'
          ? 'Rejected'
          : contact?.ats_stage || '-'}
      </KText>
    );
  }

  if (
    contact?.outreach_candidate_stage !== 'candidate_not_interested' &&
    contact?.outreach_candidate_stage !== 'candidate_interested'
  ) {
    return (
      <Flex alignItems="center">
        <KText
          mr="3px"
          size="sm-regular"
          p={isFromAts && '2px 8px'}
          border={isFromAts && '1px solid'}
          borderColor={isFromAts && 'border-color'}
          borderRadius={isFromAts && '6px'}
        >
          Pending Interest
        </KText>
      </Flex>
    );
  }
  if (contact?.outreach_candidate_stage === 'candidate_not_interested') {
    return (
      <Flex alignItems="center">
        <KText
          mr="3px"
          size="sm-regular"
          p={isFromAts && '2px 8px'}
          border={isFromAts && '1px solid'}
          borderColor={isFromAts && 'border-color'}
          borderRadius={isFromAts && '6px'}
        >
          Not Interested
        </KText>
      </Flex>
    );
  }

  if (
    outreach?.status === 'completed' ||
    !canUpdateCandidateStage ||
    isCandidateAnonymized(contact)
  ) {
    return (
      <Flex
        alignItems="center"
        p={isFromAts && '2px 8px'}
        border={isFromAts && '1px solid'}
        borderColor={isFromAts && 'border-color'}
        borderRadius={isFromAts && '6px'}
        width="fit-content"
      >
        <Box
          width="6px"
          height="6px"
          bg={stagesList[stage]?.color}
          borderRadius="full"
          marginRight="8px"
        />
        <KText mr="3px"> {stagesList[stage]?.label || '-'}</KText>
      </Flex>
    );
  }

  return (
    <>
      <Flex>
        <Menu isLazy>
          <MenuButton textAlign="left">
            {contact?.outreach_candidate_stage === 'candidate_interested' &&
            !stage ? (
              <Flex alignItems="center">
                {isFromAts ? (
                  <Flex
                    alignItems="center"
                    p="2px 8px"
                    border="1px solid"
                    borderColor="border-color"
                    borderRadius="6px"
                  >
                    <Box
                      width="6px"
                      height="6px"
                      bg={stagesList?.candidate_interested?.color}
                      borderRadius="full"
                      marginRight="8px"
                    />
                    <KText mr="3px" size="sm-medium">
                      Select ATS stage
                    </KText>
                  </Flex>
                ) : (
                  <>
                    <Box
                      width="6px"
                      height="6px"
                      bg={stagesList?.candidate_interested?.color}
                      borderRadius="full"
                      marginRight="8px"
                    />
                    <KText mr="3px">Select ATS stage</KText>
                  </>
                )}
                <Icon as={ChevronDownIcon} />
              </Flex>
            ) : (
              <Flex alignItems="center">
                {isFromAts ? (
                  <Flex
                    alignItems="center"
                    p="2px 8px"
                    border="1px solid"
                    borderColor="border-color"
                    borderRadius="6px"
                  >
                    <Box
                      width="6px"
                      height="6px"
                      bg={stagesList[stage]?.color}
                      borderRadius="full"
                      marginRight="8px"
                    />
                    <KText mr="3px" size="sm-medium">
                      {stagesList[stage]?.label}
                    </KText>
                  </Flex>
                ) : (
                  <>
                    <Box
                      width="6px"
                      height="6px"
                      bg={stagesList[stage]?.color}
                      borderRadius="full"
                      marginRight="8px"
                    />
                    <KText mr="3px"> {stagesList[stage]?.label}</KText>
                  </>
                )}
                <Icon as={ChevronDownIcon} />
              </Flex>
            )}
          </MenuButton>
          <MenuList height="300px" width="275px" overflow="auto">
            <StageList
              list={list}
              selected={new Set([stage])}
              stageGroupList={stageGroupList}
              type="updatable"
              onSelect={onChange}
              isFromAts={isFromAts}
            />
          </MenuList>
        </Menu>
      </Flex>
      {isOpen && (
        <StageConfirmation
          isOpen={isOpen}
          onClose={onClose}
          onSaveSuccess={onSaveSuccess}
          fromStage={stage}
          targetStage={targetStage}
          candidate={contact}
          outreach={outreach}
        />
      )}
    </>
  );
};

export const FlowStage = ({
  currStage,
  contact,
  outreach,
  onStageUpdate = () => {},
  showRestart = true,
  openCandidateSlider = () => {},
  isFromAts = false,
  rejectionList = {},
  canEditStage = true,
}) => {
  const [stage, setStage] = useState(currStage);
  const [targetStage, setTargetStage] = useState(null);
  const {
    isOpen: isIntrestedOpen,
    onOpen: onInterestedOpen,
    onClose: onInterestedClose,
  } = useDisclosure();
  const {
    isOpen: isNotInterestedOpen,
    onOpen: onNotInterestedOpen,
    onClose: onNotInterestedClose,
  } = useDisclosure();
  const {
    isOpen: isStopOpen,
    onOpen: onStopOpen,
    onClose: onStopClose,
  } = useDisclosure();

  const canUpdateCandidateStage = usePermission({
    permission: 'contact.stage?',
  });

  const flowStageList = groupBy(userUpdatableFlowStage, 'group');
  const list = Object.entries(flowStageList);
  const onChange = (stage) => {
    if (stage?.id === currStage) return;
    if (stage?.id === 'candidate_interested') {
      setTargetStage(stage);
      onInterestedOpen();
    }
    if (stage?.id === 'candidate_not_interested') {
      setTargetStage(stage);
      onNotInterestedOpen();
    }
  };

  const onSaveSuccess = () => {
    setStage(() => targetStage?.id);
    onStageUpdate();
  };

  if (!canEditStage) {
    if (!flowStagesList[stage]) {
      return (
        <Flex
          p="2px 8px"
          border="1px solid"
          borderColor="border-color"
          borderRadius="6px"
          alignItems="center"
          gap="6px"
          w="fit-content"
        >
          <Box
            width="6px"
            height="6px"
            bg={'secondary-500'}
            borderRadius="full"
          />

          <KText flex="1" size="sm-medium" color="secondary-700">
            Flow Inactive
          </KText>
        </Flex>
      );
    }

    return (
      <Flex
        p="2px 8px"
        border="1px solid"
        borderColor="border-color"
        borderRadius="6px"
        alignItems="center"
        gap="6px"
        w="fit-content"
      >
        <Box
          width="6px"
          height="6px"
          bg={flowStagesList[stage]?.color}
          borderRadius="full"
        />

        <KText flex="1" size="sm-medium" color="secondary-700">
          {flowStagesList[stage]?.label}
        </KText>
      </Flex>
    );
  }

  if (!currStage && contact?.halt_reason !== 'application_in_ats_job') {
    return (
      <Flex alignItems="center">
        {isFromAts ? (
          <Flex
            p="2px 8px"
            border="1px solid"
            borderColor="border-color"
            borderRadius="6px"
            alignItems="center"
            gap="6px"
            w="fit-content"
          >
            <Box
              width="6px"
              height="6px"
              bg={'secondary-500'}
              borderRadius="full"
            />

            <KText flex="1" size="sm-medium" color="secondary-700">
              Flow Inactive
            </KText>
          </Flex>
        ) : (
          <KText mr="3px" color="secondary-700">
            {' '}
            Flow Inactive
          </KText>
        )}
      </Flex>
    );
  }

  if (
    outreach?.status === 'completed' ||
    !canUpdateCandidateStage ||
    !canEditStage
  ) {
    return (
      <Flex alignItems="center">
        {isFromAts ? (
          <Flex
            p="2px 8px"
            border="1px solid"
            borderColor="border-color"
            borderRadius="6px"
            alignItems="center"
          >
            <Box
              width="6px"
              height="6px"
              bg={flowStagesList[stage]?.color}
              borderRadius="full"
              marginRight="8px"
            />
            <Flex>
              <KText mr="3px" size="sm-medium" color="secondary-700">
                {flowStagesList[stage]?.label}
              </KText>
              <Icon
                as={ChevronDownIcon}
                display="none"
                _groupHover={{ display: 'block' }}
                color="secondary-700"
                mt="2px"
              />
            </Flex>
          </Flex>
        ) : (
          <>
            <Box
              width="6px"
              height="6px"
              bg={flowStagesList[stage]?.color}
              borderRadius="full"
              marginRight="8px"
            />
            <Flex>
              <KText mr="3px" size="sm-medium" color="secondary-700">
                {flowStagesList[stage]?.label}
              </KText>
              <Icon
                as={ChevronDownIcon}
                display="none"
                _groupHover={{ display: 'block' }}
                color="secondary-700"
                mt="2px"
              />
            </Flex>
          </>
        )}
      </Flex>
    );
  }

  if (
    contact?.halt_reason === 'application_in_ats_job' ||
    (contact?.outreach_candidate_stage === 'paused' &&
      contact?.halt_reason?.length)
  ) {
    return (
      <>
        <FlowRestart
          contact={contact}
          onStopOpen={() => onStopOpen()}
          openCandidateSlider={openCandidateSlider}
        />
        <StopConfirmation
          isOpen={isStopOpen}
          onClose={onStopClose}
          contact={contact}
          outreach={outreach}
        />
      </>
    );
  }

  return (
    <>
      <Flex>
        <Menu isLazy>
          <MenuButton textAlign="left">
            <Flex alignItems="center">
              {isFromAts ? (
                <Flex
                  p="2px 8px"
                  border="1px solid"
                  borderColor="border-color"
                  borderRadius="6px"
                  alignItems="center"
                >
                  <Box
                    width="6px"
                    height="6px"
                    bg={flowStagesList[stage]?.color}
                    borderRadius="full"
                    marginRight="8px"
                  />
                  <Flex>
                    <KText mr="3px" size="sm-medium" color="secondary-700">
                      {flowStagesList[stage]?.label}
                    </KText>
                    <Icon as={ChevronDownIcon} color="secondary-700" mt="2px" />
                  </Flex>
                </Flex>
              ) : (
                <Flex
                  p="2px 8px"
                  border="1px solid"
                  borderColor="border-color"
                  borderRadius="6px"
                  alignItems="center"
                >
                  <Box
                    width="6px"
                    height="6px"
                    bg={flowStagesList[stage]?.color}
                    borderRadius="full"
                    marginRight="8px"
                  />
                  <Flex>
                    <KText mr="3px" color="secondary-700">
                      {' '}
                      {flowStagesList[stage]?.label}
                    </KText>
                    <Icon as={ChevronDownIcon} color="secondary-700" mt="2px" />
                  </Flex>
                </Flex>
              )}
            </Flex>
          </MenuButton>
          <MenuList height="125px" width="275px" overflow="auto">
            <StageList
              list={list}
              selected={new Set([stage])}
              stageGroupList={stageGroupList}
              type="updatable"
              onSelect={onChange}
              isFromAts={isFromAts}
            />
          </MenuList>
        </Menu>
        {(contact?.outreach_candidate_stage === 'stopped_no_email' ||
          contact?.outreach_candidate_stage === 'bounced') &&
          showRestart && <RestartOutreach contact={contact} />}
      </Flex>
      {isIntrestedOpen && (
        <ConfrimIntrested
          isOpen={isIntrestedOpen}
          onClose={onInterestedClose}
          onSaveSuccess={onSaveSuccess}
          candidate={contact}
          fromStage={currStage}
          outreach={outreach}
          targetStage={targetStage}
        />
      )}
      {isNotInterestedOpen && (
        <ConfrimNotIntrested
          isOpen={isNotInterestedOpen}
          onClose={onNotInterestedClose}
          onSaveSuccess={onSaveSuccess}
          candidate={contact}
          fromStage={currStage}
          outreach={outreach}
          targetStage={targetStage}
          rejectionListObj={rejectionList}
        />
      )}
    </>
  );
};

export const Interested = ({ contact, outreach }) => {
  const canUpdateCandidateStage = usePermission({
    permission: 'contact.stage?',
  });
  const {
    isOpen: isInterestedOpen,
    onOpen: onInterestedOpen,
    onClose: onInterestedClose,
  } = useDisclosure();
  const {
    isOpen: isNotInterestedOpen,
    onOpen: onNotInterestedOpen,
    onClose: onNotInterestedClose,
  } = useDisclosure();
  const [targetStage, setTargetStage] = useState(null);

  return (
    <Flex gap="10px">
      <Tooltip
        label="Interested"
        arrowSize={8}
        hasArrow
        placement="bottom"
        borderRadius="4px"
      >
        <Box position="relative">
          {contact?.outreach_candidate_stage === 'candidate_interested' ? (
            <Icon as={LikeSolid} width="20px" height="20px" color="#5551FF" />
          ) : (
            <Icon
              as={Like}
              width="20px"
              height="20px"
              onClick={() => {
                if (
                  outreach?.status === 'completed' ||
                  outreach?.status === 'draft' ||
                  !canUpdateCandidateStage
                )
                  return;
                onInterestedOpen();
                setTargetStage(flowStagesList?.candidate_interested);
              }}
            />
          )}
        </Box>
      </Tooltip>
      <Tooltip
        label="Not Interested"
        arrowSize={8}
        hasArrow
        placement="bottom"
        borderRadius="4px"
      >
        <Box position="relative">
          {contact?.outreach_candidate_stage === 'candidate_not_interested' ? (
            <Icon
              as={LikeSolid}
              width="20px"
              height="20px"
              transform="rotate(180deg)"
            />
          ) : (
            <Icon
              as={Like}
              width="20px"
              height="20px"
              transform="rotate(180deg)"
              onClick={() => {
                if (
                  outreach?.status === 'completed' ||
                  outreach?.status === 'draft' ||
                  !canUpdateCandidateStage
                )
                  return;
                onNotInterestedOpen();
                setTargetStage(flowStagesList?.candidate_not_interested);
              }}
            />
          )}
        </Box>
      </Tooltip>
      {isInterestedOpen && (
        <ConfrimIntrested
          isOpen={isInterestedOpen}
          onClose={onInterestedClose}
          candidate={contact}
          fromStage={contact?.outreach_candidate_stage}
          outreach={outreach}
          targetStage={targetStage}
        />
      )}
      {isNotInterestedOpen && (
        <ConfrimNotIntrested
          isOpen={isNotInterestedOpen}
          onClose={onNotInterestedClose}
          candidate={contact}
          fromStage={contact?.outreach_candidate_stage}
          outreach={outreach}
          targetStage={targetStage}
        />
      )}
    </Flex>
  );
};
